import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { products } from "@/mocks/products";

export default function ReviewsPage() {
  const [filterRating, setFilterRating] = useState<number | null>(null);
  const [filterCategory, setFilterCategory] = useState("All");

  const allReviews = useMemo(() => {
    return products.flatMap(p =>
      p.reviews.map(r => ({ ...r, productName: p.name, productId: p.id, productImage: p.images[0], productCategory: p.category }))
    );
  }, []);

  const filtered = useMemo(() => {
    let list = [...allReviews];
    if (filterRating !== null) list = list.filter(r => r.rating === filterRating);
    if (filterCategory !== "All") list = list.filter(r => r.productCategory === filterCategory);
    return list;
  }, [allReviews, filterRating, filterCategory]);

  const avgRating = (allReviews.reduce((s, r) => s + r.rating, 0) / allReviews.length).toFixed(1);
  const ratingCounts = [5,4,3,2,1].map(star => ({
    star,
    count: allReviews.filter(r => r.rating === star).length,
    pct: (allReviews.filter(r => r.rating === star).length / allReviews.length) * 100,
  }));

  const categories = ["All", ...Array.from(new Set(products.map(p => p.category)))];

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <Navbar />
      <main className="pt-[72px]">
        {/* Header */}
        <div className="bg-white border-b border-gray-100 py-10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
              <Link to="/" className="hover:text-[#2D5016] transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#1a1a1a]">Customer Reviews</span>
            </div>
            <h1 className="text-3xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Customer Reviews
            </h1>
            <p className="text-gray-400 mt-2 text-sm">Real experiences from our outdoor living community</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">
          {/* Overview */}
          <div className="bg-white rounded-2xl p-8 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-10">
              <div className="text-center flex-shrink-0">
                <div className="text-7xl font-bold text-[#1a1a1a]">{avgRating}</div>
                <div className="flex items-center justify-center gap-0.5 my-2">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className="ri-star-fill text-amber-400 text-xl"></i>
                  ))}
                </div>
                <div className="text-gray-400 text-sm">{allReviews.length} total reviews</div>
              </div>
              <div className="flex-1 w-full">
                {ratingCounts.map(({ star, count, pct }) => (
                  <button
                    key={star}
                    onClick={() => setFilterRating(filterRating === star ? null : star)}
                    className={`flex items-center gap-3 w-full mb-2 group cursor-pointer rounded-lg px-2 py-1 transition-colors ${filterRating === star ? "bg-[#EEF5E8]" : "hover:bg-gray-50"}`}
                  >
                    <span className="text-sm text-gray-500 w-4">{star}</span>
                    <i className="ri-star-fill text-amber-400 text-sm"></i>
                    <div className="flex-1 bg-gray-100 rounded-full h-2.5">
                      <div className="bg-amber-400 h-2.5 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                    </div>
                    <span className="text-sm text-gray-400 w-6 text-right">{count}</span>
                  </button>
                ))}
              </div>
              <div className="flex-shrink-0 text-center">
                <div className="text-[#2D5016] text-4xl font-bold">98%</div>
                <div className="text-gray-400 text-sm mt-1">Would recommend</div>
                <div className="mt-4 text-[#2D5016] text-3xl font-bold">500+</div>
                <div className="text-gray-400 text-sm mt-1">Happy customers</div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <span className="text-sm text-gray-400">Filter by category:</span>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCategory(cat)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  filterCategory === cat
                    ? "bg-[#2D5016] text-white"
                    : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {cat}
              </button>
            ))}
            {(filterRating !== null || filterCategory !== "All") && (
              <button
                onClick={() => { setFilterRating(null); setFilterCategory("All"); }}
                className="text-[#2D5016] text-sm hover:underline cursor-pointer whitespace-nowrap"
              >
                Clear filters
              </button>
            )}
          </div>

          <p className="text-gray-400 text-sm mb-6">{filtered.length} reviews</p>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map(review => (
              <div key={review.id} className="bg-white rounded-2xl p-6 flex flex-col">
                {/* Product */}
                <Link to={`/product/${review.productId}`} className="flex items-center gap-3 mb-4 group cursor-pointer">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#F9F7F3] flex-shrink-0">
                    <img src={review.productImage} alt={review.productName} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1a1a1a] truncate group-hover:text-[#2D5016] transition-colors">{review.productName}</p>
                    <p className="text-xs text-gray-400">{review.productCategory}</p>
                  </div>
                </Link>

                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-3">
                  {[1,2,3,4,5].map(s => (
                    <i key={s} className={`text-sm ${s <= review.rating ? "ri-star-fill text-amber-400" : "ri-star-line text-gray-200"}`}></i>
                  ))}
                </div>

                {/* Comment */}
                <p className="text-gray-600 text-sm leading-relaxed flex-1 mb-4">&ldquo;{review.comment}&rdquo;</p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
                  <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                    <img src={review.avatar} alt={review.author} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#1a1a1a]">{review.author}</p>
                    <p className="text-xs text-gray-400">{review.location} &bull; {review.date}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-star-line text-4xl mb-3 block"></i>
              <p>No reviews match your filters.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
